// A token's ability book (issue 1127; docs/VTT/design/29-the-attack-chain.md §1, §2.1): its actions — a character's
// weapons from its sheet, a monster's attacks from its stat block (the DM's) — each a button that fires the whole
// attack at this person's target in one click, the target chosen here or from the token menu, and the newest result
// for this token under them. The server reads the actions and resolves the attack; nothing here decides a number.
import { useSheetPulse } from '../../sheetPulse';
import { useEffect, useState } from 'react';
import { dmAttackLines, playerAttackLines, tokenLabel, type AttackSpec } from '@dragons-table/vtt-shared';
import { aim, useTarget } from '../../board/target';
import { fetchActions, type ActionsAnswer } from '../../entries';
import { fetchBag } from '../../dice';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useShell } from '../context';
import { PendingAttack } from './AttackParts';
import { BTN } from '../ui';
import { hotDragProps } from '../../board/hotDrag';
import { ManeuverButtons, StanceButtons } from './ManeuverParts';
import { TokenPowers } from './PowerParts';
import { StandingPowers } from './StandingParts';

const btn = BTN;

interface Thing { id: string; kind: string; name?: string; mine?: boolean; character?: string | null; entry?: unknown }

/** A token's actions, asked once per token and source; null while asked or when they cannot be read. */
export function useTokenActions(o: Thing | undefined, enabled: boolean): ActionsAnswer | 'asking' {
  const { campaignId } = useShell();
  const { scene } = useTableState();
  const [answer, setAnswer] = useState<ActionsAnswer | 'asking'>('asking');
  const key = o ? `${o.id} ${o.character ?? ''} ${JSON.stringify(o.entry ?? null)}` : '';
  // A weapon added on the sheet (qa-runs 1258) reads in quietly, with no flash of the bar asking.
  const pulse = useSheetPulse(o?.id);
  useEffect(() => {
    if (!o || !enabled || !pulse) return undefined;
    let live = true;
    void fetchActions(campaignId, o.id, scene?.id ?? null).then((a) => { if (live) setAnswer(a); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse]);
  useEffect(() => {
    if (!o || !enabled) { setAnswer(null); return undefined; }
    let live = true;
    setAnswer('asking');
    void fetchActions(campaignId, o.id, scene?.id ?? null).then((a) => { if (live) setAnswer(a); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, campaignId, scene?.id]);
  return answer;
}

/**
 * Fire an action at the target: the attack request, private for the DM unless unticked. The DM's private attack carries
 * their fudge band as any private d20 from the bag does (design/29 §2.10; the review of 2026-10-07), read from the bag
 * as it stands when the attack is made.
 */
export function useFire(): (attacker: string, target: string, spec: AttackSpec, priv: boolean, rulings?: string[], castAttack?: boolean) => void {
  const { act, setNotice } = useTableState();
  const mayAct = useMayAct();
  return (attacker, target, spec, priv, rulings = [], castAttack = false) => {
    const isDm = mayAct('attack.request');
    void (async () => {
      const band = isDm && priv ? ((await fetchBag().catch(() => null))?.band ?? null) : null;
      act({ kind: 'attack.request', attacker, target, attack: spec, private: isDm ? priv : false, ...(band ? { band } : {}), ...(rulings.length ? { rulings } : {}), ...(castAttack ? { castAttack: true } : {}) } as never, (reply) => {
        if (!reply.ok) setNotice(reply.reason ?? 'That attack could not be made.');
      });
    })();
  };
}

/** The newest attack made with this token, as this person may read it: the DM's whole line, a player's own version. */
function useLastAttack(o: Thing): string[] | null {
  const { log, role } = useTableState();
  for (let i = log.length - 1; i >= 0; i -= 1) {
    const e = log[i]!;
    if (role === 'dm' && e.kind === 'roll' && e.roll.attack?.attacker.id === o.id) return dmAttackLines(e.roll);
    if (role !== 'dm' && e.kind === 'attack' && o.name && e.attack.attacker?.name === o.name) return playerAttackLines(e.attack);
  }
  return null;
}

/** The ability book, in Properties: for a token this person may act with — their own, or any for the DM. */
export function TokenActions({ o }: { o: Thing }) {
  const { store } = useTableState();
  const isDm = useMayAct()('attack.request');
  const may = o.kind === 'token' && (isDm || o.mine === true);
  const { systemRules } = useTableState();
  // No attack the table can resolve, no ability book (issue 1305): its actions are attacks.
  const answer = useTokenActions(o, may && systemRules.attack !== null);
  const target = useTarget();
  const fire = useFire();
  const [priv, setPriv] = useState(true);
  const last = useLastAttack(o);
  if (!may || !systemRules.attack) return null;
  const tokens = Object.values(store.state.objects as Record<string, Thing>).filter((t) => t.kind === 'token' && t.id !== o.id);
  const aimed = target && target !== o.id ? tokens.find((t) => t.id === target) : undefined;
  return (
    <section className="space-y-1 rounded border border-amber-800 p-2 text-xs" data-testid="token-actions" aria-label={`Actions of ${o.name ?? 'this token'}`}>
      <div className="flex flex-wrap items-center gap-1">
        <span className="font-semibold text-amber-300">Actions</span>
        <label className="flex items-center gap-1">at
          <select aria-label="Target" className="rounded bg-stone-800 px-1 py-0.5" value={aimed?.id ?? ''} onChange={(e) => aim.set(e.target.value || null)}>
            <option value="">No target</option>
            {tokens.map((t) => <option key={t.id} value={t.id}>{tokenLabel(t, store.state.encounter) || 'Unnamed token'}</option>)}
          </select>
        </label>
        {isDm && <label className="flex items-center gap-1"><input type="checkbox" aria-label="Private" checked={priv} onChange={(e) => setPriv(e.target.checked)} />Private</label>}
      </div>
      {answer === 'asking' && <p className="text-stone-500">Reading its actions…</p>}
      {answer === null && <p className="text-stone-500">Its actions could not be read just now.</p>}
      {answer && answer !== 'asking' && (
        <>
          {answer.notice && <p className="text-amber-300">{answer.notice}</p>}
          {answer.actions.length === 0 && !answer.notice && (
            <p className="text-stone-500">{answer.from === null ? 'No sheet or stat block to read actions from: the dice bag\'s Attack… takes a typed one.' : 'No attacks on it.'}</p>
          )}
          <div className="flex flex-wrap gap-1">
            {answer.actions.map((a) => (
              <button key={a.key} type="button" className={`${btn} border-amber-700`} {...hotDragProps({ token: o.id, ref: `attack:${a.key}`, label: a.label })} disabled={!aimed} title={aimed ? `${a.spec.name} at ${aimed.name ?? 'the target'}` : 'Choose a target first: here, or Target in its menu.'}
                onClick={() => { if (aimed) fire(o.id, aimed.id, a.spec, priv); }}>{a.label}</button>
            ))}
          </div>
        </>
      )}
      <StanceButtons o={o} />
      <ManeuverButtons o={o} aimed={aimed} />
      <TokenPowers o={o} aimed={aimed} />
      <StandingPowers o={o} />
      {last && <p className="whitespace-pre-wrap break-words font-mono text-[0.6875rem] text-stone-300" data-testid="last-attack">{last.join('\n')}</p>}
      <PendingAttack attackerId={o.id} />
    </section>
  );
}
